import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit2, Trash2, Calendar, Users, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react'

export default function TaskManagementPage() {
  const navigate = useNavigate()
  const [tasks, setTasks] = useState([
    { id: 1, title: 'Design Wireframes', assignee: 'Sarah Chen', dueDate: '2026-05-15', priority: 'High', status: 'In Progress', project: 'Website Redesign', progress: 65 },
    { id: 2, title: 'API Documentation', assignee: 'Mike Johnson', dueDate: '2026-05-20', priority: 'Medium', status: 'Pending', project: 'API Integration', progress: 30 },
    { id: 3, title: 'User Testing', assignee: 'Emma Wilson', dueDate: '2026-05-25', priority: 'High', status: 'Pending', project: 'Mobile App Launch', progress: 0 },
    { id: 4, title: 'Database Schema Review', assignee: 'Alex Kumar', dueDate: '2026-05-18', priority: 'Critical', status: 'In Progress', project: 'API Integration', progress: 80 },
  ])

  const [showModal, setShowModal] = useState(false)
  const [editingTask, setEditingTask] = useState(null)
  const [formData, setFormData] = useState({
    title: '',
    assignee: '',
    dueDate: '',
    priority: 'Medium',
    status: 'Pending',
    project: '',
    progress: 0
  })

  const stats = useMemo(() => ({
    total: tasks.length,
    inProgress: tasks.filter(t => t.status === 'In Progress').length,
    completed: tasks.filter(t => t.status === 'Completed').length,
    overdue: tasks.filter(t => new Date(t.dueDate) < new Date() && t.status !== 'Completed').length
  }), [tasks])

  const handleCreateTask = () => {
    setEditingTask(null)
    setFormData({ title: '', assignee: '', dueDate: '', priority: 'Medium', status: 'Pending', project: '', progress: 0 })
    setShowModal(true)
  }

  const handleEditTask = (task) => {
    setEditingTask(task)
    setFormData(task)
    setShowModal(true)
  }

  const handleSaveTask = () => {
    if (!formData.title || !formData.assignee) { alert('Title and assignee required'); return }

    if (editingTask) {
      setTasks(tasks.map(t => t.id === editingTask.id ? { ...t, ...formData } : t))
    } else {
      setTasks([...tasks, { id: Date.now(), ...formData }])
    }
    setShowModal(false)
  }

  const handleDeleteTask = (id) => {
    if (confirm('Delete this task?')) {
      setTasks(tasks.filter(t => t.id !== id))
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-red-600/20 text-red-400'
      case 'High': return 'bg-orange-600/20 text-orange-400'
      case 'Medium': return 'bg-yellow-600/20 text-yellow-400'
      case 'Low': return 'bg-green-600/20 text-green-400'
      default: return 'bg-slate-600/20 text-slate-400'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-600/20 text-green-400'
      case 'In Progress': return 'bg-blue-600/20 text-blue-400'
      case 'Pending': return 'bg-slate-600/20 text-slate-400'
      default: return 'bg-slate-600/20 text-slate-400'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/projects')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Project Management
        </button>
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Task Management</h1>
              <p className="text-slate-400">Track and manage team tasks</p>
            </div>
            <button onClick={handleCreateTask} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold">
              <Plus size={20} /> New Task
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Tasks</div>
            <div className="text-3xl font-bold text-blue-400">{stats.total}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">In Progress</div>
            <div className="text-3xl font-bold text-yellow-400">{stats.inProgress}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Completed</div>
            <div className="text-3xl font-bold text-green-400">{stats.completed}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Overdue</div>
            <div className="text-3xl font-bold text-red-400">{stats.overdue}</div>
          </div>
        </div>

        <div className="space-y-4">
          {tasks.map((task) => (
            <div key={task.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{task.title}</h3>
                    <span className={`px-3 py-1 rounded text-xs font-semibold ${getPriorityColor(task.priority)}`}>{task.priority}</span>
                    <span className={`px-3 py-1 rounded text-xs font-semibold ${getStatusColor(task.status)}`}>{task.status}</span>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">Project: {task.project}</p>

                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-400">Progress</span>
                      <span className="text-sm font-bold text-white">{task.progress}%</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div className="h-full rounded-full bg-blue-500" style={{ width: `${task.progress}%` }}></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-slate-400">
                      <Calendar size={16} /> {new Date(task.dueDate).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-2 text-slate-400">
                      <Users size={16} /> {task.assignee}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => handleEditTask(task)} className="p-2 hover:bg-blue-500/20 rounded transition-all">
                    <Edit2 size={18} className="text-blue-400" />
                  </button>
                  <button onClick={() => handleDeleteTask(task.id)} className="p-2 hover:bg-red-500/20 rounded transition-all">
                    <Trash2 size={18} className="text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <h2 className="text-2xl font-bold text-white mb-6">{editingTask ? 'Edit Task' : 'Create New Task'}</h2>
              <div className="space-y-4 mb-6">
                <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="Task Title" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <input type="text" value={formData.assignee} onChange={(e) => setFormData({...formData, assignee: e.target.value})} placeholder="Assignee" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <input type="text" value={formData.project} onChange={(e) => setFormData({...formData, project: e.target.value})} placeholder="Project" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <div className="grid grid-cols-2 gap-4">
                  <input type="date" value={formData.dueDate} onChange={(e) => setFormData({...formData, dueDate: e.target.value})} className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white" />
                  <input type="number" value={formData.progress} onChange={(e) => setFormData({...formData, progress: parseInt(e.target.value)})} placeholder="Progress %" className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white" min="0" max="100" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                    <option>Low</option>
                    <option>Medium</option>
                    <option>High</option>
                    <option>Critical</option>
                  </select>
                  <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                    <option>Pending</option>
                    <option>In Progress</option>
                    <option>Completed</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowModal(false)} className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-semibold">Cancel</button>
                <button onClick={handleSaveTask} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold">Save Task</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
